import { useState, useEffect } from 'react';
import { client } from '@/api/client';
import { supabase } from '@/api/supabaseClient';
import { useAuth } from '@/lib/AuthContext';
import { toast } from 'sonner';
import { Save, Loader2, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import TrimbleAddressAutocomplete from '@/components/TrimbleAddressAutocomplete';

const COMPANY_KEYS = [
  { key: 'company_name', label: 'Carrier / Company Name' },
  { key: 'usdot_number', label: 'USDOT Number' },
  { key: 'mc_number', label: 'MC Number' },
  { key: 'company_address', label: 'Company Address' },
  { key: 'company_city_state', label: 'City, State, ZIP' },
  { key: 'company_phone', label: 'Company Phone Number' },
  { key: 'company_email', label: 'Company Email' },
  { key: 'company_website', label: 'Website (optional)' },
];

// Empty default for the company info form. Used both as the seed value
// for useState and as the baseline that loadSettings populates.
const EMPTY_COMPANY = {
  company_name: '', usdot_number: '', mc_number: '',
  company_address: '', company_city_state: '', company_phone: '',
  company_email: '', company_website: '',
};

// Shallow object comparison via JSON serialization. Adequate for these
// flat string-keyed forms; deep equality libraries would be overkill.
const sameShape = (a, b) => JSON.stringify(a) === JSON.stringify(b);

export default function CompanySettingsTab() {
  // refreshUser() in Supabase mode re-runs loadSupabaseUser() which
  // re-pulls memberships -> carriers(plan, name). Calling it after
  // saving carriers.name propagates the new name to AuthContext
  // (and therefore the live sidebar) instantly, no refresh needed.
  const { carrierId, isSupabase, refreshUser } = useAuth();

  // -- "company info" form --
  const [companyInfo, setCompanyInfo] = useState(EMPTY_COMPANY);

  // -- Dirty-state baseline. Mirrors the form's CURRENT state with the
  // value last fetched (or last successfully saved). The Save button
  // compares the live state against this baseline via sameShape(...)
  // to decide whether to be enabled. After a successful save, the
  // baseline is reset to the just-saved value so the button greys back
  // out until the user makes another edit. --
  const [initialCompanyInfo,  setInitialCompanyInfo]  = useState(EMPTY_COMPANY);

  const [settingIds, setSettingIds] = useState({});
  const [savingCompany, setSavingCompany] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    const settings = await client.entities.CompanySettings.list();
    const ids = {};
    const company = { ...EMPTY_COMPANY };

    // Seed company_name from carriers.name (set during onboarding) so
    // the field isn't empty for first-time users. CompanySettings
    // values override below if the user has saved them previously --
    // the carrier name fetch is a fallback, not the truth.
    if (isSupabase && carrierId) {
      try {
        const { data: carrierRow } = await supabase
          .from('carriers').select('name').eq('id', carrierId).maybeSingle();
        if (carrierRow?.name) company.company_name = carrierRow.name;
      } catch { /* non-fatal: fallback stays empty */ }
    }
    settings.forEach(s => {
      ids[s.key] = s.id;
      if (s.key in company) company[s.key] = s.value || '';
    });
    setSettingIds(ids);
    setCompanyInfo(company);
    // Snapshot the loaded values as the dirty-state baseline.
    setInitialCompanyInfo(company);
    setLoading(false);
  };

  const upsertKey = async (key, value, label) => {
    const existing = settingIds[key];
    if (existing) {
      await client.entities.CompanySettings.update(existing, { key, value: String(value), label });
    } else {
      const created = await client.entities.CompanySettings.create({ key, value: String(value), label });
      setSettingIds(prev => ({ ...prev, [key]: created.id }));
    }
  };

  const handleSaveCompany = async () => {
    setSavingCompany(true);
    try {
      // 1. Write all company fields to the legacy CompanySettings
      //    key/value entity (used by various PDF/settlement components).
      await Promise.all(
        COMPANY_KEYS.map(({ key, label }) => upsertKey(key, companyInfo[key] || '', label))
      );

      // 2. Dynamic-name sync: company_name is the canonical source for
      //    sidebar branding via AuthContext.carrierName -> carriers.name.
      //    Always push when there's a non-empty value (don't gate on
      //    "name changed" -- gating was brittle: any glitch in the
      //    prev/next comparison silently skipped the sidebar refresh).
      //    Use .select() so the UPDATE returns the affected row, which
      //    proves RLS allowed the write. If 0 rows came back, the
      //    update silently failed and we throw a loud error rather
      //    than letting the user think the sidebar will update.
      const nextName = (companyInfo.company_name || '').trim();
      if (isSupabase && carrierId && nextName) {
        console.log('[CompanySettings] writing carriers.name ->', nextName);
        const { data: updatedRows, error: nameError } = await supabase
          .from('carriers')
          .update({ name: nextName })
          .eq('id', carrierId)
          .select('id, name');
        if (nameError) throw nameError;
        if (!updatedRows || updatedRows.length === 0) {
          throw new Error(
            'Could not update carrier name (RLS rejected the write or carrier_id mismatch). Sidebar will stay stale until a page reload.'
          );
        }
        console.log('[CompanySettings] carriers.name updated:', updatedRows[0]);
      }

      // 3. ALWAYS refresh the global auth state after a successful save
      //    (not just on name change). refreshUser() re-runs
      //    loadSupabaseUser() which re-pulls memberships ->
      //    carriers(plan, name) -> AuthContext.carrierName -> Layout
      //    sidebar re-renders. Cost is one extra query when nothing
      //    name-related changed; benefit is bulletproof sidebar sync.
      if (isSupabase) {
        console.log('[CompanySettings] refreshing global auth state...');
        await refreshUser();
        console.log('[CompanySettings] refresh complete -- sidebar should reflect new carrier name');
      }

      // 4. Reset the dirty-state baseline so the Save button greys back
      //    out until the user makes another edit.
      setInitialCompanyInfo({ ...companyInfo });
      toast.success('Company information saved.');
    } catch (e) {
      console.error('[CompanySettings] save failed:', e);
      toast.error(e?.message || 'Could not save company information.');
    } finally {
      setSavingCompany(false);
    }
  };

  // Dirty-state flag. The Save button is enabled only when the live
  // state differs from the loaded/last-saved baseline. Compared with
  // sameShape (JSON.stringify) -- the form is a flat string-keyed
  // object so this is fast and correct.
  const isCompanyDirty = !sameShape(companyInfo, initialCompanyInfo);

  if (loading) {
    return (
      <div className="glass-card p-12 flex justify-center">
        <div className="w-6 h-6 border-2 border-border border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  const inputClass = "w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50";

  return (
    <div className="space-y-4 max-w-2xl">

      {/* Company Information */}
      <div className="glass-card p-6 space-y-4">
        <div className="flex items-center gap-3 pb-3 border-b border-border">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <Building2 className="w-4 h-4 text-primary" />
          </div>
          <div>
            <h3 className="font-space font-semibold text-foreground">Company Information</h3>
            <p className="text-xs text-muted-foreground">Carrier identity details used on all pay stubs and settlement documents</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {COMPANY_KEYS.map(({ key, label }) => {
            // Company Address gets the same Trimble-powered autocomplete
            // already used for Load pickup/dropoff, Address Book contacts,
            // and onboarding -- picking a suggestion fills City/State/ZIP
            // too, which stays a separate, still-editable field below.
            if (key === 'company_address') {
              return (
                <div key={key}>
                  <TrimbleAddressAutocomplete
                    value={companyInfo.company_address}
                    onChange={e => setCompanyInfo(prev => ({ ...prev, company_address: e.target.value }))}
                    onSelect={data => setCompanyInfo(prev => ({
                      ...prev,
                      company_address: data.street || prev.company_address,
                      company_city_state: [data.city, data.state, data.zip].filter(Boolean).join(', ') || prev.company_city_state,
                    }))}
                    label={label}
                    placeholder={label}
                  />
                </div>
              );
            }
            return (
              <div key={key}>
                <label className="text-xs text-muted-foreground block mb-1.5">{label}</label>
                <input
                  type={key === 'company_email' ? 'email' : 'text'}
                  value={companyInfo[key]}
                  onChange={e => setCompanyInfo(prev => ({ ...prev, [key]: e.target.value }))}
                  placeholder={label}
                  className={inputClass}
                />
              </div>
            );
          })}
        </div>

        <p className="text-xs text-muted-foreground mt-1">This information appears on all driver settlement statements and pay stubs.</p>

        {/* Dirty-state Save: disabled (greyed) when the form matches the
            last-loaded/saved baseline; Pro Haul Red when dirty; "Saving..."
            with spinner during the in-flight request. */}
        <Button
          onClick={handleSaveCompany}
          disabled={!isCompanyDirty || savingCompany}
          className={
            isCompanyDirty
              ? 'bg-prohaul-red hover:bg-prohaul-red/90 text-white'
              : 'bg-muted text-muted-foreground cursor-not-allowed'
          }
        >
          {savingCompany
            ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
            : <><Save className="w-4 h-4 mr-2" /> Save Company Info</>}
        </Button>
      </div>
    </div>
  );
}